'use client';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { motion } from 'framer-motion';
import Carousel from 'nuka-carousel';
import PhotoSwipe from 'photoswipe';
import 'photoswipe/style.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

/* ------------------------------ Anim helpers ------------------------------ */

const normalizeAnimation = (raw) => {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'none',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'none';
};

const mapGroup = (raw) => {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (['media', 'medias', 'images', 'image', 'features'].includes(v))
        return 'features';
    return 'content';
};

const getElementVariants = (preset, duration) => {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 16 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 0.96 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { type: 'tween', duration: d } },
    };
};

const getStaggerContainer = (duration) => {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
};

function MotionMaybe({
    enable,
    k,
    motionProps = {},
    className,
    children,
    ...rest
}) {
    if (enable) {
        return (
            <motion.div
                key={`${k || 'motion'}-on`}
                className={className}
                {...motionProps}
                {...rest}
            >
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k || 'motion'}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
            {...rest}
        >
            {children}
        </div>
    );
}

const LIGHTBOX_CLASS = 'pswp-gallery-root';

export default function Gallery(props) {
    const {
        MainHeader,
        Tagline,
        Images,
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        OutlineButtonText,
        SecondaryButtonLink,
        OutlineButtonLink,
        ColorScheme = 'light',
        VerticalSpacing = 'medium',
        ImageVariant = 'masonry',
        Animation = 'none',
        Duration = 0.5,
        AnimationGroup = 'content',
    } = props;

    const vSpace = getVerticalSpacing(VerticalSpacing);

    const [aspectRatios, setAspectRatios] = useState({});
    const [slidesToShow, setSlidesToShow] = useState(3);
    const normalizedVariant = useMemo(() => {
        const variant = String(ImageVariant || 'masonry').toLowerCase();
        if (variant.includes('slider-full')) return 'slider-full';
        if (variant.includes('slider')) return 'slider';
        if (variant.includes('bento')) return 'bento';
        if (variant.includes('square')) return 'square';
        if (variant.includes('contain')) return 'contain';
        if (variant.includes('masonry')) return 'masonry';
        return variant || 'masonry';
    }, [ImageVariant]);
    const preset = normalizeAnimation(Animation);
    const group = mapGroup(AnimationGroup);
    const duration = Math.max(0.2, Number(Duration) || 0.5);
    const isStagger = preset === 'stagger';
    const enableAnim = preset !== 'none';
    const animateContent = enableAnim && group === 'content';
    const animateFeatures = enableAnim && group === 'features';
    const viewport = { once: true, amount: 0.3 };
    const {
        backgroundColor,
        taglineColor,
        headerColor,
        thirdButton,
        secondaryButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);
    const displayImages = useMemo(() => {
        const baseImages =
            normalizedVariant === 'bento'
                ? Images?.slice(0, 9) || []
                : Images || [];

        const safeList =
            Array.isArray(baseImages) && baseImages.length > 0
                ? baseImages
                : [
                      {
                          ImageUrl: '/images/placeholder.svg',
                          AltText: 'Placeholder image',
                      },
                  ];

        return safeList.map((img) => ({
            ...img,
            ImageUrl: img?.ImageUrl || '/images/placeholder.svg',
        }));
    }, [Images, normalizedVariant]);
    const motionImageStyle = {
        WebkitTransformStyle: 'preserve-3d',
        WebkitBackfaceVisibility: 'hidden',
        transformStyle: 'preserve-3d',
        backfaceVisibility: 'hidden',
    };
    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: 'justify-center',
            wrapSpacing: 'mt-10 lg:mt-16',
            className: 'relative z-40',
            buttonsClassName:
                'flex flex-wrap items-center justify-center gap-4',
        }
    );

    useEffect(
        () => () => {
            pswpRef.current?.destroy();
            pswpRef.current = null;
        },
        []
    );

    useEffect(() => {
        let isActive = true;

        const loadImage = (url) => {
            return new Promise((resolve) => {
                if (!url) return resolve(null);

                const img = new Image();
                img.onload = () =>
                    resolve({
                        width: img.naturalWidth || img.width,
                        height: img.naturalHeight || img.height,
                    });
                img.onerror = () => resolve(null);
                img.src = url;
            });
        };

        const calculateAspectRatios = async () => {
            if (!Array.isArray(displayImages) || displayImages.length === 0) {
                if (isActive) setAspectRatios({});
                return;
            }
            const ratios = {};
            for (const img of displayImages) {
                const dimensions = await loadImage(img.ImageUrl);
                if (dimensions) {
                    ratios[img.ImageUrl] = dimensions;
                }
            }
            if (isActive) setAspectRatios(ratios);
        };

        calculateAspectRatios();

        return () => {
            isActive = false;
        };
    }, [displayImages]);

    useEffect(() => {
        const updateSlidesToShow = () => {
            if (window.innerWidth < 640) {
                setSlidesToShow(1);
            } else if (window.innerWidth < 1024) {
                setSlidesToShow(2);
            } else {
                setSlidesToShow(3);
            }
        };

        updateSlidesToShow();
        window.addEventListener('resize', updateSlidesToShow);

        return () => {
            window.removeEventListener('resize', updateSlidesToShow);
        };
    }, []);

    let galleryClass, imageClass, shape, containHeightClass;
    switch (normalizedVariant) {
        case 'square':
            galleryClass =
                'mx-auto flex max-w-6xl flex-wrap items-start justify-center gap-2 lg:gap-4';
            imageClass = 'object-cover h-full w-full';
            shape = 'rounded-[var(--shape-radius)]';
            break;
        case 'contain':
            galleryClass =
                'mx-auto flex max-w-5xl flex-wrap items-center gap-1 justify-center';
            containHeightClass = 'h-48 sm:h-48 lg:h-48';
            imageClass =
                'block h-full w-auto max-w-full object-contain mx-auto';
            shape = 'rounded-[min(1.5rem,var(--shape-radius))]';
            break;
        case 'bento':
            galleryClass =
                'grid auto-rows-[6.5rem] sm:auto-rows-[6.5rem] md:auto-rows-[8rem] lg:auto-rows-[12rem] gap-4 grid-cols-3 md:grid-cols-6 sm:grid-cols-6 lg:grid-cols-6';
            imageClass = 'object-cover h-full w-full';
            shape = 'rounded-[min(1.5rem,var(--shape-radius))]';
            break;
        case 'slider':
            galleryClass = 'max-w-7xl mx-auto';
            imageClass = 'h-64 w-full object-cover';
            shape = 'rounded-[min(1.5rem,var(--shape-radius))]';
            break;
        case 'slider-full':
            galleryClass = 'mx-auto';
            imageClass = 'h-[500px] w-full object-cover';
            shape = '';
            break;
        default: // masonry
            galleryClass = 'columns-1 sm:columns-2 md:columns-3 lg:columns-5';
            imageClass = 'object-cover mb-4 break-inside-avoid';
            shape = 'rounded-[min(1.5rem,var(--shape-radius))]';
            break;
    }

    const bentoSpans = (index) => {
        if (normalizedVariant !== 'bento') return '';

        const spans = [
            'sm:col-span-2 col-span-2',
            'sm:col-span-1 col-span-1',
            'sm:col-span-1 sm:row-span-2 col-span-1 row-span-2',
            'sm:col-span-1 sm:row-span-2 col-span-1 row-span-2',
            'sm:col-span-1 col-span-1',
            'sm:col-span-3 sm:row-span-2 col-span-1 row-span-2',
            'sm:col-span-1 sm:row-span-1 col-span-2 row-span-2',
            'sm:col-span-1 sm:row-span-1 col-span-1 row-span-2',
            'sm:col-span-2 col-span-2',
        ];

        return spans[index] || '';
    };

    const getImageMeta = (image) => {
        const ratioData = image?.ImageUrl ? aspectRatios[image.ImageUrl] : null;
        const width = Number(ratioData?.width);
        const height = Number(ratioData?.height);
        const hasValidDimensions =
            Number.isFinite(width) &&
            Number.isFinite(height) &&
            width > 0 &&
            height > 0;
        const safeAspectRatio = hasValidDimensions ? width / height : 1;

        return {
            pswpWidth: hasValidDimensions ? width : 1000,
            pswpHeight: hasValidDimensions ? height : 1000 / safeAspectRatio,
            aspectStyle: hasValidDimensions
                ? { aspectRatio: `${width}/${height}` }
                : undefined,
        };
    };

    const staggerContainerVariants = useMemo(
        () => (isStagger ? getStaggerContainer(duration) : null),
        [isStagger, duration]
    );

    const buildItemMotion = (shouldAnimate) => {
        if (!shouldAnimate) return {};
        if (isStagger) {
            const childVariants = {
                hidden: { opacity: 0, y: 14 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: {
                        duration: Math.max(0.25, duration - 0.1),
                        ease: 'easeOut',
                    },
                },
            };
            return {
                variants: childVariants,
                initial: 'hidden',
                whileInView: 'show',
                viewport,
            };
        }
        const v = getElementVariants(preset, duration);
        const hasOffscreen = v && 'offscreen' in v;
        return {
            variants: v,
            initial: hasOffscreen ? 'offscreen' : 'hidden',
            whileInView: hasOffscreen ? 'onscreen' : 'show',
            viewport,
        };
    };

    const buildContainerMotion = (shouldAnimate) => {
        if (!shouldAnimate || !staggerContainerVariants) return {};
        return {
            variants: staggerContainerVariants,
            initial: 'hidden',
            whileInView: 'show',
            viewport,
        };
    };

    const contentContainerMotionProps = buildContainerMotion(animateContent);
    const featureContainerMotionProps = buildContainerMotion(animateFeatures);
    const contentItemMotionProps = buildItemMotion(animateContent);
    const featureItemMotionProps = buildItemMotion(animateFeatures);
    const pswpRef = useRef(null);
    const captionStylesInjected = useRef(false);

    const itemsForLightbox = useMemo(() => {
        return displayImages.map((img) => {
            const { pswpHeight, pswpWidth } = getImageMeta(img);
            return {
                src: img.ImageUrl,
                width: pswpWidth,
                height: pswpHeight,
                alt: img.AltText || '',
                caption: img.Caption || img.AltText || '',
            };
        });
    }, [displayImages, aspectRatios]);

    const ensureCaptionStyles = () => {
        if (captionStylesInjected.current || typeof document === 'undefined')
            return;
        const style = document.createElement('style');
        style.id = 'pswp-custom-caption-style';
        style.textContent = `
            .pswp__custom-caption {
                position: absolute;
                left: 50%;
                bottom: 28px;
                transform: translateX(-50%);
                padding: 10px 20px;
                color: #1f2937; /* neutral-800 */
                font-size: 14px;
                line-height: 1.5;
                background: #fff;
                border-radius: 9999px;
                box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
                box-sizing: border-box;
                z-index: 30;
                max-width: calc(100% - 32px);
                text-align: center;
            }
            .pswp__custom-caption:empty { display: none; }
            .pswp__custom-caption a { color: #1f2937; text-decoration: underline; }
            @media (max-width: 640px) {
                .pswp__custom-caption {
                    font-size: 12px;
                    padding: 8px 14px;
                    bottom: 22px;
                }
            }
        `;
        document.head.appendChild(style);
        captionStylesInjected.current = true;
    };

    const handleImageClick = (index) => async (e) => {
        e.preventDefault();
        ensureCaptionStyles();
        if (pswpRef.current) {
            pswpRef.current.destroy();
            pswpRef.current = null;
        }
        const instance = new PhotoSwipe({
            dataSource: itemsForLightbox,
            index,
            paddingFn: () => ({
                top: 72,
                bottom: 72,
                left: 48,
                right: 48,
            }),
        });
        instance.on('uiRegister', () => {
            instance.ui.registerElement({
                name: 'custom-caption',
                order: 9,
                isButton: false,
                appendTo: 'wrapper',
                className: 'pswp__custom-caption',
                onInit: (el, pswp) => {
                    const update = () => {
                        const cap = pswp.currSlide?.data?.caption || '';
                        el.innerHTML = cap || '';
                    };
                    pswp.on('change', update);
                    pswp.on('afterInit', update);
                },
            });
        });
        instance.on('destroy', () => {
            pswpRef.current = null;
        });
        instance.init();
        pswpRef.current = instance;
    };

    return (
        <div data-json="gallery">
            <div
                className={`editable-component relative ${backgroundColor} overflow-hidden ${
                    normalizedVariant === 'slider-full'
                        ? 'py-0'
                        : `px-6 lg:px-8 ${vSpace}`
                }`}
                style={backgroundStyle}
            >
                {MainHeader && (
                    <MotionMaybe
                        enable={animateContent && enableAnim}
                        className="relative z-40 mx-auto px-6 pb-16 sm:max-w-xl md:max-w-full md:px-24 lg:max-w-screen-xl lg:px-8"
                        motionProps={contentContainerMotionProps}
                    >
                        <MotionMaybe
                            enable={animateContent && enableAnim}
                            className="mb-6 max-w-xl text-center md:mx-auto md:mb-10 lg:max-w-2xl"
                            motionProps={contentItemMotionProps}
                        >
                            <div>
                                <MarkDown
                                    className={`font-display text-base uppercase leading-7 ${taglineColor} tracking-wide`}
                                >
                                    {Tagline}
                                </MarkDown>
                            </div>
                            <h2
                                className={`mt-2 font-display text-3xl tracking-tight ${headerColor} sm:text-4xl sm:leading-tight`}
                            >
                                <MarkDown>{MainHeader}</MarkDown>
                            </h2>
                        </MotionMaybe>
                    </MotionMaybe>
                )}

                {normalizedVariant === 'slider' ? (
                    <MotionMaybe
                        id="slider-gallery"
                        className={`${galleryClass} ${LIGHTBOX_CLASS}`}
                        enable={animateFeatures && enableAnim && isStagger}
                        motionProps={featureContainerMotionProps}
                    >
                        <Carousel
                            renderCenterLeftControls={({
                                previousSlide,
                                previousDisabled,
                            }) => (
                                <button
                                    className={`fa-solid fa-angle-left relative p-10 text-3xl text-neutral-800 opacity-30 transition-opacity duration-500 ease-in-out hover:opacity-100`}
                                    onClick={previousSlide}
                                    disabled={previousDisabled}
                                    aria-label="Previous Slide"
                                ></button>
                            )}
                            renderCenterRightControls={({
                                nextSlide,
                                nextDisabled,
                            }) => (
                                <button
                                    className={`fa-solid fa-angle-right relative p-10 text-3xl text-neutral-800 opacity-30 transition-opacity duration-500 ease-in-out hover:opacity-100`}
                                    onClick={nextSlide}
                                    disabled={nextDisabled}
                                    aria-label="Next Slide"
                                ></button>
                            )}
                            renderBottomCenterControls={() => <div></div>}
                            cellAlign="center"
                            cellSpacing={8}
                            className={`w-full`}
                            disableEdgeSwiping={true}
                            withoutControls={false}
                            pauseOnHover={true}
                            autoplay={true}
                            autoplayInterval={5000}
                            wrapAround={true}
                            slidesToShow={slidesToShow}
                        >
                            {displayImages.map((Showcase, index) => {
                                const { pswpHeight, pswpWidth } =
                                    getImageMeta(Showcase);
                                const { x: imagePosX, y: imagePosY } =
                                    parseImagePosition(Showcase.ImagePosition);
                                const imageMotionStyle = {
                                    ...motionImageStyle,
                                    objectPosition: `${imagePosX}% ${imagePosY}%`,
                                };
                                return (
                                    <MotionMaybe
                                        key={index}
                                        className="p-2"
                                        enable={animateFeatures && enableAnim}
                                        motionProps={featureItemMotionProps}
                                    >
                                        <a
                                            href={Showcase.ImageUrl}
                                            data-pswp-src={Showcase.ImageUrl}
                                            data-pswp-width={pswpWidth}
                                            data-pswp-height={pswpHeight}
                                            onClick={handleImageClick(index)}
                                        >
                                            <motion.img
                                                src={Showcase.ImageUrl}
                                                alt={Showcase.AltText}
                                                className={`${imageClass} ${shape} ring-neutral-400/10`}
                                                style={imageMotionStyle}
                                                whileHover={{ scale: 1.05 }}
                                            />
                                        </a>
                                    </MotionMaybe>
                                );
                            })}
                        </Carousel>
                    </MotionMaybe>
                ) : normalizedVariant === 'slider-full' ? (
                    <MotionMaybe
                        id="slider-gallery"
                        className={`${galleryClass} ${LIGHTBOX_CLASS}`}
                        enable={animateFeatures && enableAnim && isStagger}
                        motionProps={featureContainerMotionProps}
                    >
                        <Carousel
                            renderCenterLeftControls={({
                                previousSlide,
                                previousDisabled,
                            }) => (
                                <button
                                    className={`fa-solid fa-angle-left relative p-10 text-3xl text-neutral-800 opacity-30 transition-opacity duration-500 ease-in-out hover:opacity-100`}
                                    onClick={previousSlide}
                                    disabled={previousDisabled}
                                    aria-label="Previous Slide"
                                ></button>
                            )}
                            renderCenterRightControls={({
                                nextSlide,
                                nextDisabled,
                            }) => (
                                <button
                                    className={`fa-solid fa-angle-right relative p-10 text-3xl text-neutral-800 opacity-30 transition-opacity duration-500 ease-in-out hover:opacity-100`}
                                    onClick={nextSlide}
                                    disabled={nextDisabled}
                                    aria-label="Next Slide"
                                ></button>
                            )}
                            renderBottomCenterControls={() => <div></div>}
                            cellAlign="center"
                            cellSpacing={0}
                            className={`w-full`}
                            disableEdgeSwiping={true}
                            withoutControls={false}
                            pauseOnHover={true}
                            autoplay={true}
                            autoplayInterval={5000}
                            wrapAround={true}
                            slidesToShow={slidesToShow}
                        >
                            {displayImages.map((Showcase, index) => {
                                const { pswpHeight, pswpWidth } =
                                    getImageMeta(Showcase);
                                const { x: imagePosX, y: imagePosY } =
                                    parseImagePosition(Showcase.ImagePosition);
                                const imageMotionStyle = {
                                    ...motionImageStyle,
                                    objectPosition: `${imagePosX}% ${imagePosY}%`,
                                };
                                return (
                                    <MotionMaybe
                                        key={index}
                                        className="p-0"
                                        enable={animateFeatures && enableAnim}
                                        motionProps={featureItemMotionProps}
                                    >
                                        <a
                                            href={Showcase.ImageUrl}
                                            data-pswp-src={Showcase.ImageUrl}
                                            data-pswp-width={pswpWidth}
                                            data-pswp-height={pswpHeight}
                                            onClick={handleImageClick(index)}
                                        >
                                            <motion.img
                                                src={Showcase.ImageUrl}
                                                alt={Showcase.AltText}
                                                className={`${imageClass} ${shape} ring-neutral-400/10`}
                                                style={imageMotionStyle}
                                                whileHover={{ scale: 1.05 }}
                                            />
                                        </a>
                                    </MotionMaybe>
                                );
                            })}
                        </Carousel>
                    </MotionMaybe>
                ) : (
                    <MotionMaybe
                        id="gallery"
                        className={`relative z-40 flex justify-center ${LIGHTBOX_CLASS}`}
                        enable={animateFeatures && enableAnim && isStagger}
                        motionProps={featureContainerMotionProps}
                    >
                        <MotionMaybe
                            className={`mx-auto w-full max-w-7xl ${galleryClass}`}
                            enable={animateFeatures && enableAnim && isStagger}
                            motionProps={featureContainerMotionProps}
                        >
                            {displayImages.map((Showcase, index) => {
                                const { aspectStyle, pswpHeight, pswpWidth } =
                                    getImageMeta(Showcase);
                                const { x: imagePosX, y: imagePosY } =
                                    parseImagePosition(Showcase.ImagePosition);
                                const imageMotionStyle = {
                                    ...motionImageStyle,
                                    objectPosition: `${imagePosX}% ${imagePosY}%`,
                                };

                                if (normalizedVariant === 'square') {
                                    return (
                                        <MotionMaybe
                                            key={index}
                                            className={`relative h-40 w-40 overflow-hidden sm:h-44 sm:w-44 md:h-48 md:w-48 ${shape}`}
                                            enable={
                                                animateFeatures && enableAnim
                                            }
                                            motionProps={featureItemMotionProps}
                                        >
                                            <a
                                                href={Showcase.ImageUrl}
                                                data-pswp-src={
                                                    Showcase.ImageUrl
                                                }
                                                data-pswp-width={pswpWidth}
                                                data-pswp-height={pswpHeight}
                                                className="absolute inset-0 block"
                                                onClick={handleImageClick(
                                                    index
                                                )}
                                            >
                                                <motion.img
                                                    src={Showcase.ImageUrl}
                                                    alt={Showcase.AltText}
                                                    className={`h-full w-full ${imageClass}`}
                                                    style={imageMotionStyle}
                                                    whileHover={{
                                                        scale: 1.05,
                                                    }}
                                                />
                                            </a>
                                        </MotionMaybe>
                                    );
                                } else if (normalizedVariant === 'contain') {
                                    return (
                                        <MotionMaybe
                                            key={index}
                                            className={`flex justify-center p-4 ${
                                                containHeightClass ?? ''
                                            }`}
                                            enable={
                                                animateFeatures && enableAnim
                                            }
                                            motionProps={featureItemMotionProps}
                                        >
                                            <a
                                                href={Showcase.ImageUrl}
                                                data-pswp-src={
                                                    Showcase.ImageUrl
                                                }
                                                data-pswp-width={pswpWidth}
                                                data-pswp-height={pswpHeight}
                                                className="inline-block h-full"
                                                onClick={handleImageClick(
                                                    index
                                                )}
                                            >
                                                <motion.img
                                                    src={Showcase.ImageUrl}
                                                    alt={Showcase.AltText}
                                                    className={`${imageClass} ${shape} ring-neutral-400/10`}
                                                    style={{
                                                        ...imageMotionStyle,
                                                        ...aspectStyle,
                                                    }}
                                                    whileHover={{ scale: 1.05 }}
                                                />
                                            </a>
                                        </MotionMaybe>
                                    );
                                } else {
                                    return (
                                        <MotionMaybe
                                            key={index}
                                            className={`break-inside-avoid ${bentoSpans(
                                                index
                                            )} md:${bentoSpans(index)}`}
                                            enable={
                                                animateFeatures && enableAnim
                                            }
                                            motionProps={featureItemMotionProps}
                                        >
                                            <a
                                                href={Showcase.ImageUrl}
                                                data-pswp-src={
                                                    Showcase.ImageUrl
                                                }
                                                data-pswp-width={pswpWidth}
                                                data-pswp-height={pswpHeight}
                                                onClick={handleImageClick(
                                                    index
                                                )}
                                            >
                                                <motion.img
                                                    src={Showcase.ImageUrl}
                                                    alt={Showcase.AltText}
                                                    className={`w-full ${imageClass} ${shape} ring-neutral-400/10`}
                                                    style={imageMotionStyle}
                                                    whileHover={{ scale: 1.05 }}
                                                />
                                            </a>
                                        </MotionMaybe>
                                    );
                                }
                            })}
                        </MotionMaybe>
                    </MotionMaybe>
                )}

                {hasCTAs && CTAs}
            </div>
        </div>
    );
}
